/**
 * tmm-reseller-admin Material UI theme. The mechanism is ported from dink-admin
 * (DNKADM-0266/0269/0297/0299); the VALUES are a neutral genesis palette
 * (MUI blue brand, gray surface ladder). Re-skin by editing `buildTokens`
 * and `./tokens` — nothing else.
 *
 *  - `AdminTokens` is the semantic token namespace, exposed on every built
 *    theme as `theme.tokens` (module augmentation below).
 *  - `buildTokens(mode)` returns the per-mode token values.
 *  - `buildAdminTheme(mode)` wires the MUI palette + every component override
 *    from tokens, so both the palette-driven surfaces and the overrides below
 *    flip per mode.
 *  - `resolveTokens(theme)` reads `theme.tokens` with a safe fallback for a
 *    bare `createTheme()` (e.g. component tests that don't mount the app
 *    theme) — shared primitives read tokens through this, never
 *    `theme.tokens.*` directly.
 *
 * CONVENTION (enforced by the `no-restricted-syntax` color guard in
 * `eslint.config.js`, ported from dink-admin DNKADM-0269): feature and route
 * surfaces read `theme.tokens.*` via `resolveTokens(useTheme())` and the
 * `inkAlpha(theme, α)` escape hatch — never raw hex/rgba literals.
 *
 * Stack: `@mui/material` + `@emotion`. Mounted once app-wide via
 * `AdminThemeProvider` (ThemeModeProvider → `useMemo(buildAdminTheme)` →
 * `ThemeProvider` + `CssBaseline`) in `main.tsx`. The mode preference persists
 * under the `tmm_reseller_admin.themeMode` storage key (see
 * `src/context/ThemeModeContext.tsx`).
 */
import { createTheme, type Theme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import { BRAND, BRAND_DARK, INTER_FEATURES, MONO, PAGE_BG, RADII, SANS, SHADOWS } from './tokens';

// ---- Typography augmentation: typed `mono` and `kpi` treatments ----
// Identifiers (slugs, codes, DNS values, references) render in JetBrains Mono as
// <Typography variant="mono"> / theme.typography.mono (TYPE-007). `kpi` is the
// headline-number treatment, with tabular numerals (TYPE-006, TMMRSL-0043).
declare module '@mui/material/styles' {
  interface TypographyVariants {
    mono: CSSProperties;
    kpi: CSSProperties;
  }
  interface TypographyVariantsOptions {
    mono?: CSSProperties;
    kpi?: CSSProperties;
  }
  interface Theme {
    tokens: AdminTokens;
  }
  interface ThemeOptions {
    tokens?: AdminTokens;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    mono: true;
    kpi: true;
  }
}

// ---- The semantic token namespace (DNKADM-0266; TMM-T4 role names, TMMRSL-0068) ----
// Non-MUI design tokens, exposed via `theme.tokens` so components read e.g.
// `theme.tokens.line` instead of magic hex/rgba. The role names are the TMM profile's (TMM-T4,
// tmm-app's vocabulary): `bg`, `surface*`, `text*`, `link`, `line*`, `focusRing`, `brand*`, and the
// `{success,warning,error,info}×{Fg,Text,Fill,Ink,Soft}` set. A field marked "console extra (no
// TMM-T4 role)" has no profile equivalent and keeps its console name.
export interface AdminTokens {
  /** Page background (body / `background.default`). */
  bg: string;
  /** Card/paper/AppBar surface (`background.paper`). */
  surface: string;
  /** Subtly tinted surface — table heads. */
  surface2: string;
  /** Raised inset surface — the nav's current-user chip, the toast. */
  surfaceRaised: string;
  /** Standard divider / border (`palette.divider`). */
  line: string;
  /** Stronger control border (ToggleButton). */
  lineStrong: string;
  /** Console extra (no TMM-T4 role): soft hairline — logo lockup / user-chip separators. */
  lineSoft: string;
  /** Console extra (no TMM-T4 role): faintest row separator — table body cell borders. */
  lineFaint: string;
  /** Primary text (`text.primary`). */
  text: string;
  /** Secondary text (`text.secondary`). */
  text2: string;
  /** Disabled text (`text.disabled`). */
  text3: string;
  /** Console extra (no TMM-T4 role): subtle metadata text — the user chip's role label. */
  textSubtle: string;
  /** Console extra (no TMM-T4 role): muted label text — nav section labels. */
  textMuted: string;
  /** Console extra (no TMM-T4 role): ghost/decorative glyphs — the locked-nav padlocks. */
  textGhost: string;
  /** Brand — FIXED across modes (neutral genesis: MUI blue). */
  brand: string;
  /** Link text — brand in light mode, contrast-safe tint in dark mode. */
  link: string;
  /** Brand hover/pressed — FIXED across modes. */
  brandHover: string;
  /** Readable ink on a brand fill — FIXED across modes. */
  brandInk: string;
  /** Brand-tinted fill — active nav item background. */
  brandSoft: string;
  /** Console extra (no TMM-T4 role): stronger brand tint — active-nav hover / front-desk chip. */
  brandSoft2: string;
  /** Console extra (no TMM-T4 role): faint brand wash — nav item hover. */
  brandFaint: string;
  /** Brand-tinted border (reserved for per-surface conversions). */
  brandBoundary: string;
  /** Success icon or graphic on a surface (`palette.success.main`). */
  successFg: string;
  /** Success-coloured text on a surface or on `successSoft` (AA). */
  successText: string;
  /** Solid success background — the "prices this business" chip. */
  successFill: string;
  /** Ink on `successFill`. */
  successInk: string;
  /** Success alert/chip surface. */
  successSoft: string;
  /** Console extra (no TMM-T4 role): hover fill of a filled success control (`palette.success.dark`). */
  successHover: string;
  /** Warning icon or graphic on a surface (`palette.warning.main`). */
  warningFg: string;
  /** Warning-coloured text on a surface or on `warningSoft` (AA). */
  warningText: string;
  /** Warning-tinted background — the nav Confirmations count badge, the Staging chip. */
  warningFill: string;
  /** Ink on `warningFill`. */
  warningInk: string;
  /** Warning alert surface. */
  warningSoft: string;
  /** Console extra (no TMM-T4 role): warning alert border. */
  warnSoftLine: string;
  /** Console extra (no TMM-T4 role): hover fill of a filled warning control (`palette.warning.dark`). */
  warningHover: string;
  /** Error foreground for icons and graphics on a neutral surface (`palette.error.main`). */
  errorFg: string;
  /** Error-coloured text on a surface or on `errorSoft` (AA). */
  errorText: string;
  /** Error background for destructive controls whose foreground is `brandInk`. */
  errorFill: string;
  /** Error alert surface. */
  errorSoft: string;
  /** Console extra (no TMM-T4 role): error alert border. */
  errorSoftLine: string;
  /** Console extra (no TMM-T4 role): hover fill of a filled error control (`palette.error.dark`). */
  errorHover: string;
  /** Info accent (reference only — `palette.info` keeps MUI's per-mode default). */
  infoFg: string;
  /** Info-coloured text on `infoSoft` (AA). */
  infoText: string;
  /** Info alert surface. */
  infoSoft: string;
  /** Console extra (no TMM-T4 role): card shadow. */
  shadowCard: string;
  /** Console extra (no TMM-T4 role): dialog shadow. */
  shadowDialog: string;
  /** Console extra (no TMM-T4 role): AppBar shadow. */
  shadowAppBar: string;
  /** Keyboard focus ring (A11Y-002): 3:1 against `surface` and `bg`. */
  focusRing: string;
}

/**
 * Per-mode token values. Neutral genesis ladder — re-skin here.
 *
 * Where two pre-0068 console roles merged into one profile role (`successDark` + `successSoftInk`
 * → `successText`, likewise warning and error), the higher-contrast value is kept: in light the
 * darker ink, in dark the lighter ink (the dark `*Dark` values failed AA as text on the soft
 * surface, so they stay only as the `*Hover` fills they were).
 */
export function buildTokens(mode: 'light' | 'dark'): AdminTokens {
  if (mode === 'dark') {
    return {
      brand: BRAND,
      brandHover: BRAND_DARK,
      brandInk: '#FFFFFF',
      link: '#64B5F6',
      bg: '#0F1114',
      surface: '#16191D',
      surface2: '#1A1E23',
      surfaceRaised: '#21262C',
      line: '#2A3037',
      lineStrong: '#5F6771',
      lineSoft: '#252B31',
      lineFaint: '#22272D',
      text: '#ECEEF1',
      text2: '#A7AEB7',
      text3: '#7F8791',
      textSubtle: '#8E96A0',
      textMuted: '#828A94',
      textGhost: '#5F6771',
      brandSoft: 'rgba(25,118,210,0.28)',
      brandSoft2: 'rgba(25,118,210,0.38)',
      brandFaint: 'rgba(25,118,210,0.14)',
      brandBoundary: 'rgba(25,118,210,0.5)',
      successFg: '#4CAF50',
      successText: '#A5D6A7',
      successFill: '#4CAF50',
      successInk: '#0F1114',
      successSoft: 'rgba(76,175,80,0.18)',
      successHover: '#388E3C',
      warningFg: '#FFA726',
      warningText: '#FFCC80',
      warningFill: 'rgba(255,167,38,0.22)',
      warningInk: '#FFD599',
      warningSoft: 'rgba(255,167,38,0.16)',
      warnSoftLine: 'rgba(255,167,38,0.4)',
      warningHover: '#F57C00',
      errorFg: '#EF5350',
      errorText: '#EF9A9A',
      errorFill: '#D32F2F',
      errorSoft: 'rgba(239,83,80,0.18)',
      errorSoftLine: 'rgba(239,83,80,0.4)',
      errorHover: '#B71C1C',
      infoFg: '#29B6F6',
      infoText: '#90CAF9',
      infoSoft: 'rgba(41,182,246,0.16)',
      shadowCard: '0 1px 3px rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.4)',
      shadowDialog: '0 11px 15px -7px rgba(0,0,0,0.5), 0 24px 38px 3px rgba(0,0,0,0.4)',
      shadowAppBar: '0 1px 2px rgba(0,0,0,0.3)',
      focusRing: '#64B5F6',
    };
  }

  return {
    brand: BRAND,
    brandHover: BRAND_DARK,
    brandInk: '#FFFFFF',
    link: '#1565C0',
    bg: PAGE_BG,
    surface: '#FFFFFF',
    surface2: '#F7F8FA',
    surfaceRaised: '#EEF0F3',
    line: '#E1E4E8',
    lineStrong: '#C9CED5',
    lineSoft: '#E9ECEF',
    lineFaint: '#F0F2F4',
    text: '#1A1D21',
    text2: '#5C6470',
    text3: '#8E959F',
    textSubtle: '#5F6772',
    textMuted: '#68707C',
    textGhost: '#B5BBC4',
    brandSoft: '#E3EEFA',
    brandSoft2: '#D2E4F7',
    brandFaint: '#EFF5FC',
    brandBoundary: 'rgba(25,118,210,0.3)',
    successFg: '#2E7D32',
    successText: '#1B5E20',
    successFill: '#2E7D32',
    successInk: '#FFFFFF',
    successSoft: '#E8F5E9',
    successHover: '#1B5E20',
    warningFg: '#ED6C02',
    warningText: '#7A3E00',
    warningFill: '#FFF3CD',
    warningInk: '#6B5200',
    warningSoft: '#FFF4E5',
    warnSoftLine: '#F7D9B8',
    warningHover: '#B85A00',
    errorFg: '#D32F2F',
    errorText: '#8E2A2A',
    errorFill: '#D32F2F',
    errorSoft: '#FDECEA',
    errorSoftLine: '#F5C6C6',
    errorHover: '#9A1F1F',
    infoFg: '#0288D1',
    infoText: '#2A5A7E',
    infoSoft: '#E8F1F8',
    shadowCard: SHADOWS.card,
    shadowDialog: SHADOWS.dialog,
    shadowAppBar: '0 1px 2px rgba(0,0,0,0.04)',
    focusRing: BRAND,
  };
}

/**
 * Read the design tokens off a theme, with a safe fallback. The app always
 * supplies `theme.tokens` via `buildAdminTheme`, but a bare `createTheme()`
 * (e.g. a component test that doesn't wrap in the app theme) won't — so shared
 * primitives resolve tokens through this instead of crashing on
 * `theme.tokens.*`.
 */
export function resolveTokens(theme: Theme): AdminTokens {
  return (
    (theme as { tokens?: AdminTokens }).tokens ??
    buildTokens(theme.palette.mode === 'dark' ? 'dark' : 'light')
  );
}

/** The dark-mode neutral ink base (the dark `text` family, as an alpha base). */
const DARK_INK_BASE = '236,238,241';

/**
 * Mode-aware neutral ink at an EXACT alpha (DNKADM-0269) — the sweep's escape
 * hatch for `rgba(0,0,0,α)` literals whose exact value has no semantic token.
 * Light output is byte-identical to the swept literal (`rgba(0,0,0,α)` — the
 * 0266 identity constraint); dark swaps the base to the dark ink family at the
 * same alpha. Prefer a real token (`text/2/3`, `line*`) when the alpha matches
 * one; use this only for the odd in-between values.
 */
export function inkAlpha(theme: Theme, alpha: number): string {
  return theme.palette.mode === 'dark' ? `rgba(${DARK_INK_BASE},${alpha})` : `rgba(0,0,0,${alpha})`;
}
/**
 * Turn Inter's `cv05` / `cv08` on for every UI-face typography variant (TMM-T1). MUI components
 * spread their variant (inputs `body1`, buttons `button`, chips and tooltips `body2`, ...), so the
 * variants carry the features to the components that set their own font; `mono` is JetBrains Mono
 * and is left alone. It still inherits the body's `cv05` / `cv08`, which is harmless: the bundled
 * JetBrains Mono files (400/500/600) have no such features (`interFeatures.test.tsx` scans them, so
 * a face that does would fail it and `mono` would need `fontFeatureSettings: 'normal'`).
 */
function withInterFeatures<T extends Record<string, unknown>>(variants: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(variants)) {
    out[key] =
      key !== 'mono' && typeof value === 'object' && value !== null
        ? { ...value, fontFeatureSettings: INTER_FEATURES }
        : value;
  }
  return out as T;
}

/** Build the mode-aware MUI theme — every color below reads from tokens. */
export function buildAdminTheme(mode: 'light' | 'dark'): Theme {
  const t = buildTokens(mode);

  return createTheme({
    tokens: t,
    palette: {
      mode,
      primary: { main: t.brand, dark: t.brandHover, contrastText: t.brandInk },
      // `successInk` is the ink on a solid success fill: the page-dark ink in dark mode (those
      // fills are bright), white in light (MUI's own computed contrastText there).
      success: { main: t.successFg, dark: t.successHover, contrastText: t.successInk },
      warning: { main: t.warningFg, dark: t.warningHover },
      error: { main: t.errorFg, dark: t.errorHover },
      background: { default: t.bg, paper: t.surface },
      text: {
        primary: t.text,
        secondary: t.text2,
        disabled: t.text3,
      },
      divider: t.line,
    },

    // `RADII.control`; ALSO the multiplier base for every numeric
    // `sx.borderRadius` in the app — do not change it (see RADII's docblock).
    shape: { borderRadius: RADII.control },

    // The scale is TMM-T2 (profile 1.0; UI standard v1.3 TYPE-002/004/005): h1 28/36/600, h2 18/24/600,
    // h3 16/22/600, body1 and body2 14/20/400, caption 12/16/400, overline 12/16/600, kpi 24/30/600 —
    // every variant states its line height, as px strings so they are exact. `body2` carries no colour:
    // a secondary line sets `color: 'text.secondary'` where it is written. h4–h6 are not TMM-T2
    // variants; they are mapped to h3's size (h6 renders dialog and drawer titles). Every UI-face
    // variant turns on Inter's `cv05` / `cv08` (TMM-T1); `mono` is JetBrains Mono, which has neither (the
    // body's features reach it by inheritance and do nothing).
    typography: withInterFeatures({
      fontFamily: SANS,
      // Page heading.
      h1: { fontSize: 28, lineHeight: '36px', fontWeight: 600, letterSpacing: '-0.02em' },
      // Section heading.
      h2: { fontSize: 18, lineHeight: '24px', fontWeight: 600, letterSpacing: '-0.01em' },
      // Sub-section heading.
      h3: { fontSize: 16, lineHeight: '22px', fontWeight: 600 },
      // Not TMM-T2 variants: mapped to h3 (h4 and h5 are unused; h6 is the dialog / drawer title).
      h4: { fontSize: 16, lineHeight: '22px', fontWeight: 600 },
      h5: { fontSize: 16, lineHeight: '22px', fontWeight: 600 },
      h6: { fontSize: 16, lineHeight: '22px', fontWeight: 600 },
      // Card headings.
      subtitle1: { fontSize: 16, lineHeight: '22px', fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle2: { fontSize: 14, lineHeight: '20px', fontWeight: 600 },
      // Inputs and list items.
      body1: { fontSize: 14, lineHeight: '20px', fontWeight: 400 },
      body2: { fontSize: 14, lineHeight: '20px', fontWeight: 400 },
      caption: { fontSize: 12, lineHeight: '16px', fontWeight: 400 },
      // Short metadata labels only, never a section heading (TYPE-009).
      overline: {
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      button: {
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: 0,
      },
      // Identifiers (TYPE-007): slugs, codes, DNS values, references.
      mono: {
        fontFamily: MONO,
        fontSize: 13,
        lineHeight: '20px',
        letterSpacing: 0,
      },
      // Headline numbers (KPI tiles, cost cards), tabular so they compare (TYPE-006).
      kpi: {
        fontSize: 24,
        lineHeight: '30px',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        fontVariantNumeric: 'tabular-nums',
      },
    }),

    components: {
      // Page background; the keyboard focus ring (A11Y-002) and reduced motion (A11Y-009).
      MuiCssBaseline: {
        styleOverrides: {
          // Inter's character variants (TMM-T1): cv05 tailed l, cv08 serifed I — I / l / 1 stay distinct.
          body: { backgroundColor: t.bg, fontFeatureSettings: INTER_FEATURES },
          'a:focus-visible, button:focus-visible, [tabindex]:focus-visible': {
            outline: `2px solid ${t.focusRing}`,
            outlineOffset: 2,
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
        },
      },

      // The focus ring on every MUI control's keyboard focus (A11Y-002).
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': { outline: `2px solid ${t.focusRing}`, outlineOffset: 2 },
          },
        },
      },

      MuiLink: {
        styleOverrides: {
          root: { color: t.link },
        },
      },

      // Cards / Paper: 12px radius + the handoff's layered card shadow.
      // `backgroundImage: 'none'` kills MUI's dark-mode elevation overlay so
      // paper surfaces stay token-exact (a no-op in light, which has none).
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: RADII.card },
          elevation1: { boxShadow: t.shadowCard },
        },
      },

      MuiAppBar: {
        defaultProps: { color: 'inherit', elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: t.surface,
            borderBottom: `1px solid ${t.line}`,
            boxShadow: t.shadowAppBar,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          // The width is the shell's `LAYOUT.drawerWidth`.
          paper: { borderInlineEnd: `1px solid ${t.line}` },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            fontFeatureSettings: INTER_FEATURES,
            borderRadius: RADII.button,
            minHeight: 38,
            paddingInline: 16,
            // v9: the removed `containedPrimary` compound-class slot moves to a
            // `variants` entry (variant: 'contained' + color: 'primary').
            variants: [
              {
                props: { variant: 'contained', color: 'primary' },
                style: { '&:hover': { backgroundColor: t.brandHover } },
              },
              {
                // TMMRSL-0073 (axe color-contrast): a text or outlined primary button's label is the
                // AA link colour, not the brand fill (#1976D2 is under 4.5:1 on the page and on dark
                // surfaces).
                props: { variant: 'text', color: 'primary' },
                style: { color: t.link },
              },
              {
                props: { variant: 'outlined', color: 'primary' },
                style: { color: t.link },
              },
              {
                // DNKADM-0330: `palette.error.main` is the lifted foreground
                // role. Filled destructive controls retain the darker fill so
                // their white ink also remains AA.
                props: { variant: 'contained', color: 'error' },
                style: {
                  backgroundColor: t.errorFill,
                  color: t.brandInk,
                  '&:hover': { backgroundColor: t.errorHover },
                },
              },
            ],
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          // `pill` renders identically to the old 12 at the 24px height
          // (both are a full capsule) but stays a capsule at any height.
          root: {
            height: 24,
            borderRadius: RADII.pill,
            fontSize: 12,
            fontWeight: 500,
            fontFeatureSettings: INTER_FEATURES,
            variants: [
              {
                props: { variant: 'filled', color: 'error' },
                style: { backgroundColor: t.errorFill, color: t.brandInk },
              },
              {
                props: { variant: 'filled', color: 'success' },
                style: { backgroundColor: t.successFill, color: t.successInk },
              },
              {
                // TMMRSL-0073 (axe color-contrast): white on MUI's info fill is under 4.5:1; the
                // soft info pair (as the info Alert's) is AA in both modes.
                props: { variant: 'filled', color: 'info' },
                style: { backgroundColor: t.infoSoft, color: t.infoText },
              },
              {
                // TMMRSL-0074 (axe color-contrast): white on MUI's warning fill (#ED6C02) is 3.11:1
                // at the 12px chip. The soft warning pair (as the warning Alert's) is AA in both modes.
                props: { variant: 'filled', color: 'warning' },
                style: { backgroundColor: t.warningSoft, color: t.warningText },
              },
            ],
          },
          label: { paddingInline: 10 },
        },
      },

      MuiBadge: {
        styleOverrides: {
          badge: {
            '&.MuiBadge-colorError': {
              backgroundColor: t.errorFill,
              color: t.brandInk,
            },
          },
        },
      },

      // Small tables by default: 14/20 values with 9px vertical padding keep rows about 38px
      // (DEN-007 Standard). A right-aligned cell holds a number, so it is tabular (TYPE-006).
      MuiTable: { defaultProps: { size: 'small' } },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: 14,
            lineHeight: '20px',
            paddingTop: 9,
            paddingBottom: 9,
            borderColor: t.lineFaint,
            variants: [
              { props: { align: 'right' }, style: { fontVariantNumeric: 'tabular-nums' } },
            ],
          },
          head: {
            fontSize: 12,
            lineHeight: '16px',
            fontWeight: 600,
            color: t.text2,
            backgroundColor: t.surface2,
            borderBottomColor: t.line,
          },
        },
      },

      // Inputs and tooltips set their own font; both carry Inter's variants (TMM-T1).
      MuiInputBase: { styleOverrides: { root: { fontFeatureSettings: INTER_FEATURES } } },
      MuiTooltip: { styleOverrides: { tooltip: { fontFeatureSettings: INTER_FEATURES } } },

      MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADII.control,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: t.brand,
            },
          },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 14,
            paddingInline: 14,
            borderColor: t.lineStrong,
            '&.Mui-selected': {
              backgroundColor: t.brand,
              color: t.brandInk,
              '&:hover': { backgroundColor: t.brandHover },
            },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: RADII.sheet, boxShadow: t.shadowDialog },
        },
      },

      // Tonal alerts for empty / permission / error states.
      MuiAlert: {
        styleOverrides: {
          // v9: the removed `standard{Error,Warning,Success,Info}` compound-class
          // slots move to `variants` entries (variant: 'standard' + each color).
          root: {
            borderRadius: RADII.row,
            fontSize: 14,
            lineHeight: '20px',
            alignItems: 'flex-start',
            variants: [
              {
                props: { variant: 'standard', severity: 'error' },
                style: {
                  backgroundColor: t.errorSoft,
                  color: t.errorText,
                  border: `1px solid ${t.errorSoftLine}`,
                },
              },
              {
                props: { variant: 'standard', severity: 'warning' },
                style: {
                  backgroundColor: t.warningSoft,
                  color: t.warningText,
                  border: `1px solid ${t.warnSoftLine}`,
                },
              },
              {
                props: { variant: 'standard', severity: 'success' },
                style: {
                  backgroundColor: t.successSoft,
                  color: t.successText,
                },
              },
              {
                props: { variant: 'standard', severity: 'info' },
                style: { backgroundColor: t.infoSoft, color: t.infoText },
              },
            ],
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          // Sentence case with the uppercase-era tracking dropped, matching
          // the button treatment (DNKADM-0299 U3, the approved design).
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: 0,
            minHeight: 46,
            // TMMRSL-0073 (axe color-contrast): the selected tab's label in the AA link colour; the
            // indicator keeps the brand.
            '&.Mui-selected': { color: t.link },
          },
        },
      },

      // TMMRSL-0073 (axe color-contrast): a disabled field's legend and helper text stay readable
      // (secondary text, AA) — MUI's disabled grey is under 4.5:1, and a legend is not a control.
      MuiFormLabel: {
        styleOverrides: { root: { '&.Mui-disabled': { color: t.text2 } } },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { '&.Mui-disabled': { color: t.text2 } } },
      },

      MuiSkeleton: { defaultProps: { animation: 'pulse' } },
    },
  });
}

/**
 * The static light theme — `buildAdminTheme('light')`, byte-identical to the
 * pre-0266 static export. Kept so existing `import { theme } from '@/theme'`
 * call sites (tests, non-mode-aware wrappers) keep working; the app itself
 * mounts the mode-aware theme via `AdminThemeProvider`.
 */
export const theme = buildAdminTheme('light');
